import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, Settings as SettingsIcon, RefreshCw, X, Smartphone, Globe } from "lucide-react";
import { useT } from "../lib/lang";

/**
 * Non-blocking modal that surfaces clear instructions whenever the
 * browser/device geolocation is OFF or denied. The modal:
 *   1. Re-checks permission on mount + every time the visible tab
 *      regains focus (so a user who toggled GPS in OS settings sees
 *      the modal disappear without reloading).
 *   2. Offers a "Réessayer" CTA that fires `getCurrentPosition` again
 *      to either grant the permission or surface the OS-level error.
 *   3. Can be dismissed; it auto-reappears next time the app starts
 *      OR the user navigates back to the SOS home page without a fix.
 */
export const GpsPermissionGate = () => {
  const [t] = useT();
  const [status, setStatus] = useState("checking"); // checking | granted | prompt | denied | unsupported | unavailable
  const [dismissed, setDismissed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const location = useLocation();

  // Re-show the prompt every time the user lands on the home SOS page
  // without a working fix, even after a previous dismiss.
  useEffect(() => {
    if (location.pathname === "/") Promise.resolve().then(() => setDismissed(false));
  }, [location.pathname]);

  const probe = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setStatus("unsupported");
      return;
    }
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((s) => {
          setStatus(s.state); // "prompt" | "granted" | "denied"
          // Wire one-shot change listener so we hide the modal the moment
          // the user flips the permission in browser UI.
          s.onchange = () => setStatus(s.state);
        })
        .catch(() => setStatus("prompt"));
    } else {
      // Safari/old WebKit: no Permissions API → probe with a real call.
      navigator.geolocation.getCurrentPosition(
        () => setStatus("granted"),
        (err) => setStatus(err?.code === 1 ? "denied" : "unavailable"),
        { enableHighAccuracy: false, timeout: 6000, maximumAge: 60000 },
      );
    }
  }, []);

  useEffect(() => {
    Promise.resolve().then(probe);
    const onVisible = () => {
      if (document.visibilityState === "visible") probe();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [probe]);

  const retry = () => {
    if (!("geolocation" in navigator)) return;
    setRetrying(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        setStatus("granted");
        setRetrying(false);
      },
      (err) => {
        setStatus(err?.code === 1 ? "denied" : "unavailable");
        setRetrying(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  // Modal only shows for blocking states, on the home SOS route, when
  // the user has not explicitly dismissed it for this session.
  const isHome = location.pathname === "/";
  const needsAttention = status === "denied" || status === "unavailable" || status === "unsupported";
  if (!isHome || !needsAttention || dismissed) return null;

  const headline =
    status === "denied" ? t.gpsBlockedTitle
      : status === "unsupported" ? t.gpsUnsupportedTitle
      : t.gpsUnavailableTitle;

  const body =
    status === "denied" ? t.gpsBlockedBody
      : status === "unsupported" ? t.gpsUnsupportedBody
      : t.gpsUnavailableBody;

  return (
    <div
      className="fixed inset-0 z-[400] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="gps-permission-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gps-gate-title"
    >
      <div className="w-full max-w-md bg-card text-card-foreground border-2 border-border rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl">
        <div className="bg-rescue-ink text-white px-5 py-5 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-mali-yellow">
              {t.gpsRequiredKicker}
            </div>
            <h2
              id="gps-gate-title"
              className="font-display text-2xl font-black tracking-tight leading-none mt-1"
            >
              {headline}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid="gps-gate-dismiss"
            className="p-2 -m-2 text-white/70 hover:text-white transition-colors"
            aria-label={t.close || "Fermer"}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-foreground leading-relaxed">{body}</p>

          {/* Mobile (OS-level) steps */}
          <div className="border-2 border-border rounded-xl p-3 flex gap-3 bg-muted/40">
            <span className="shrink-0 w-9 h-9 rounded-full bg-mali-green/15 text-mali-green flex items-center justify-center">
              <Smartphone className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {t.gpsStepOsTitle}
              </div>
              <p className="text-xs text-foreground leading-relaxed mt-1">{t.gpsStepOsBody}</p>
            </div>
          </div>

          {/* Browser-level steps */}
          <div className="border-2 border-border rounded-xl p-3 flex gap-3 bg-muted/40">
            <span className="shrink-0 w-9 h-9 rounded-full bg-mali-yellow/25 text-amber-700 flex items-center justify-center">
              <Globe className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {t.gpsStepBrowserTitle}
              </div>
              <p className="text-xs text-foreground leading-relaxed mt-1">{t.gpsStepBrowserBody}</p>
            </div>
          </div>
        </div>

        <div className="px-5 grid grid-cols-2 gap-2 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid="gps-gate-later"
            className="bg-muted text-foreground font-bold text-xs uppercase tracking-[0.18em] py-3 rounded-xl hover:bg-muted/70 transition-colors"
          >
            {t.gpsLater}
          </button>
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            data-testid="gps-gate-retry"
            className="bg-mali-navy text-white font-bold text-xs uppercase tracking-[0.18em] py-3 rounded-xl hover:opacity-95 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${retrying ? "animate-spin" : ""}`} />
            {retrying ? t.gpsRetrying : t.gpsRetry}
          </button>
        </div>
      </div>
    </div>
  );
};
